import { useState } from 'react'
import { Product } from '../../data/products'

interface MainScreenLabProps {
  onLog: (message: string) => void
  onAddToCart: (product: Product) => void
  version: 'v1' | 'v3'
}

const colors = {
  bg: '#050508',
  cardBg: '#141418',
  nestedBg: '#1c1c22',
  accent: '#9333ea',
  accentLight: '#a855f7',
  accentBg: 'rgba(147, 51, 234, 0.12)',
  accentBorder: 'rgba(147, 51, 234, 0.25)',
  cardBorder: 'rgba(255, 255, 255, 0.12)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.52)',
  green: '#22c55e',
  red: '#ef4444',
}

// SVG Icons
const EducationIcon = ({ size = 48, color = colors.accent }: { size?: number, color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
    <path d="M6 12v5c0 1 2 3 6 3s6-2 6-3v-5"/>
  </svg>
)

const CodeIcon = ({ size = 48, color = colors.accent }: { size?: number, color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
    <polyline points="16 18 22 12 16 6"/>
    <polyline points="8 6 2 12 8 18"/>
  </svg>
)

const RobotIcon = ({ size = 48, color = colors.accent }: { size?: number, color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
    <rect x="5" y="11" width="14" height="10" rx="2"/>
    <circle cx="9" cy="15" r="1"/>
    <circle cx="15" cy="15" r="1"/>
    <path d="M8.5 8.5c-.4-.4-1-.4-1.4 0L4 11.5M15.5 8.5c.4-.4 1-.4 1.4 0L20 11.5"/>
    <path d="M12 2v6"/>
  </svg>
)

const ChevronRightIcon = ({ size = 20, color = colors.textMuted }: { size?: number, color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
)

const BackIcon = ({ size = 20, color = colors.text }: { size?: number, color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
)

// Типы для структуры данных
interface Direction {
  id: string
  name: string
  icon: 'education' | 'code' | 'robot'
  color: string
  categories: Category[]
}

interface Category {
  id: string
  name: string
  description: string
  products: Product[]
}

// Данные направлений с категориями и товарами
const directionsData: Direction[] = [
  {
    id: 'education',
    name: 'Образование',
    icon: 'education',
    color: '#3b82f6',
    categories: [
      {
        id: 'programming',
        name: 'Программирование',
        description: 'Курсы по языкам программирования',
        products: [
          { id: '1', title: 'Python для начинающих', description: 'Базовый курс Python', price: '₽12,000' },
          { id: '2', title: 'JavaScript Pro', description: 'Продвинутый JavaScript', price: '₽15,000' },
          { id: '3', title: 'Java Основы', description: 'Введение в Java', price: '₽14,000' },
        ]
      },
      {
        id: 'design',
        name: 'Дизайн',
        description: 'Курсы по графическому дизайну',
        products: [
          { id: '4', title: 'UI/UX дизайн', description: 'Полный курс по дизайну интерфейсов', price: '₽18,000' },
          { id: '5', title: 'Figma для дизайнеров', description: 'Мастерство работы в Figma', price: '₽10,000' },
        ]
      },
      {
        id: 'languages',
        name: 'Иностранные языки',
        description: 'Изучение языков онлайн',
        products: [
          { id: '6', title: 'Английский с нуля', description: 'Базовый курс английского', price: '₽8,000' },
          { id: '7', title: 'Китайский язык', description: 'Введение в китайский', price: '₽9,000' },
        ]
      },
    ]
  },
  {
    id: 'it',
    name: 'IT-разработка',
    icon: 'code',
    color: '#22c55e',
    categories: [
      {
        id: 'web',
        name: 'Web-разработка',
        description: 'Создание сайтов и веб-приложений',
        products: [
          { id: '8', title: 'React.js курс', description: 'Современная фронтенд-разработка', price: '₽16,000' },
          { id: '9', title: 'Node.js Backend', description: 'Серверная разработка на Node', price: '₽17,000' },
          { id: '10', title: 'Full Stack Developer', description: 'Полный стек разработки', price: '₽25,000' },
        ]
      },
      {
        id: 'mobile',
        name: 'Мобильная разработка',
        description: 'Создание приложений для iOS и Android',
        products: [
          { id: '11', title: 'React Native', description: 'Кросс-платформенная разработка', price: '₽18,000' },
          { id: '12', title: 'Swift для iOS', description: 'Нативная разработка для Apple', price: '₽20,000' },
        ]
      },
      {
        id: 'devops',
        name: 'DevOps',
        description: 'Автоматизация и развёртывание',
        products: [
          { id: '13', title: 'Docker и Kubernetes', description: 'Контейнеризация приложений', price: '₽15,000' },
          { id: '14', title: 'CI/CD практика', description: 'Непрерывная интеграция', price: '₽13,000' },
        ]
      },
    ]
  },
  {
    id: 'robotics',
    name: 'Робототехника',
    icon: 'robot',
    color: '#a855f7',
    categories: [
      {
        id: 'kids',
        name: 'Для детей',
        description: 'Курсы робототехники для школьников',
        products: [
          { id: '15', title: 'LEGO Robotics', description: 'Конструирование роботов из LEGO', price: '₽7,000' },
          { id: '16', title: 'Arduino для детей', description: 'Первые шаги в электронике', price: '₽8,500' },
        ]
      },
      {
        id: 'professional',
        name: 'Профессиональная',
        description: 'Курсы для специалистов',
        products: [
          { id: '17', title: 'ROS - Robot Operating System', description: 'Программирование роботов', price: '₽22,000' },
          { id: '18', title: 'Компьютерное зрение', description: 'CV для робототехники', price: '₽24,000' },
        ]
      },
      {
        id: 'ai-robotics',
        name: 'AI в робототехнике',
        description: 'Искусственный интеллект для роботов',
        products: [
          { id: '19', title: 'Machine Learning для роботов', description: 'Обучение роботов с ML', price: '₽26,000' },
          { id: '20', title: 'Нейросети в автономных системах', description: 'Автопилоты и беспилотники', price: '₽28,000' },
        ]
      },
    ]
  },
]

// Иконка для направления
const getDirectionIcon = (icon: string, size: number, color: string) => {
  switch (icon) {
    case 'education': return <EducationIcon size={size} color={color} />
    case 'code': return <CodeIcon size={size} color={color} />
    case 'robot': return <RobotIcon size={size} color={color} />
    default: return <EducationIcon size={size} color={color} />
  }
}

export default function MainScreenLab({ onLog, onAddToCart, version }: MainScreenLabProps) {
  // Навигация: null = направления, direction = категории, category = товары
  const [selectedDirection, setSelectedDirection] = useState<Direction | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)

  const handleDirectionClick = (direction: Direction) => {
    setSelectedDirection(direction)
    setSelectedCategory(null)
    onLog(`Открыто направление: ${direction.name}`)
  }

  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category)
    onLog(`Открыта категория: ${category.name}`)
  }

  const handleBack = () => {
    if (selectedCategory) {
      setSelectedCategory(null)
      onLog('Назад к категориям')
    } else if (selectedDirection) {
      setSelectedDirection(null)
      onLog('Назад к направлениям')
    }
  }

  const handleBuyProduct = (product: Product) => {
    onAddToCart(product)
    onLog(`Добавлен в корзину: ${product.title}`)
  }

  // Уровень 1: Направления (стеклянные карточки)
  if (!selectedDirection) {
    return (
      <DirectionsScreen 
        onDirectionClick={handleDirectionClick}
        version={version}
      />
    )
  }

  // Уровень 2: Категории
  if (!selectedCategory) {
    return (
      <CategoriesScreen 
        direction={selectedDirection}
        onBack={handleBack}
        onCategoryClick={handleCategoryClick}
      />
    )
  }

  // Уровень 3: Товары
  return (
    <ProductsScreen
      category={selectedCategory}
      direction={selectedDirection}
      onBack={handleBack}
      onBuyProduct={handleBuyProduct}
    />
  )
}

// Экран 1: Направления (стеклянные карточки)
function DirectionsScreen({ 
  onDirectionClick,
  version 
}: { 
  onDirectionClick: (d: Direction) => void
  version: 'v1' | 'v3'
}) {
  // V1 - классический вариант со стеклянными карточками
  if (version === 'v1') {
    return <DirectionsV1 onDirectionClick={onDirectionClick} />
  }
  
  // V3 - альтернативный дизайн
  return <DirectionsV3 onDirectionClick={onDirectionClick} />
}

// V1: Классические стеклянные карточки (вертикальные, большие)
function DirectionsV1({ onDirectionClick }: { onDirectionClick: (d: Direction) => void }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px 16px 100px',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: 700,
          color: colors.text,
          margin: '0 0 8px 0',
        }}>
          Выберите направление
        </h1>
        <p style={{
          fontSize: '14px',
          color: colors.textMuted,
          margin: 0,
        }}>
          Образование, IT или робототехника
        </p>
      </div>

      {/* Стеклянные карточки направлений */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}>
        {directionsData.map(direction => (
          <div
            key={direction.id}
            onClick={() => onDirectionClick(direction)}
            style={{
              background: `linear-gradient(135deg, ${direction.color}15, ${colors.cardBg})`,
              border: `1px solid ${direction.color}40`,
              borderRadius: '14px',
              padding: '24px',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform 0.2s',
            }}
            onMouseDown={(e) => {
              const target = e.currentTarget as HTMLDivElement
              target.style.transform = 'scale(0.98)'
            }}
            onMouseUp={(e) => {
              const target = e.currentTarget as HTMLDivElement
              target.style.transform = 'scale(1)'
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget as HTMLDivElement
              target.style.transform = 'scale(1)'
            }}
          >
            {/* Декоративный круг */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '180px',
              height: '180px',
              borderRadius: '50%',
              background: `${direction.color}20`,
              filter: 'blur(40px)',
            }} />

            {/* Содержимое карточки */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              position: 'relative',
            }}>
              {/* Иконка */}
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '12px',
                background: `${direction.color}20`,
                border: `1px solid ${direction.color}40`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {getDirectionIcon(direction.icon, 32, direction.color)}
              </div>

              {/* Текст */}
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: colors.text,
                  margin: '0 0 6px 0',
                }}>
                  {direction.name}
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: colors.textMuted,
                  margin: 0,
                }}>
                  {direction.categories.length} {direction.categories.length === 1 ? 'категория' : 'категории'}
                </p>
              </div>

              {/* Стрелка */}
              <ChevronRightIcon size={24} color={direction.color} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// V3: Альтернативный дизайн (горизонтальные карточки, компактнее)
function DirectionsV3({ onDirectionClick }: { onDirectionClick: (d: Direction) => void }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px 16px 100px',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 700,
          color: colors.text,
          margin: '0 0 6px 0',
        }}>
          Направления обучения
        </h1>
        <p style={{
          fontSize: '13px',
          color: colors.textMuted,
          margin: 0,
        }}>
          Выберите интересующую область
        </p>
      </div>

      {/* Карточки направлений - альтернативный стиль */}
      <div style={{
        display: 'grid',
        gap: '12px',
      }}>
        {directionsData.map(direction => (
          <div
            key={direction.id}
            onClick={() => onDirectionClick(direction)}
            style={{
              background: colors.cardBg,
              border: `1px solid ${direction.color}30`,
              borderRadius: '12px',
              padding: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Боковая цветная полоса */}
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              background: `linear-gradient(180deg, ${direction.color}, ${direction.color}80)`,
            }} />

            {/* Иконка */}
            <div style={{
              width: '52px',
              height: '52px',
              borderRadius: '10px',
              background: `${direction.color}18`,
              border: `1px solid ${direction.color}30`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginLeft: '8px',
            }}>
              {getDirectionIcon(direction.icon, 28, direction.color)}
            </div>

            {/* Текст */}
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: 600,
                color: colors.text,
                margin: '0 0 4px 0',
              }}>
                {direction.name}
              </h3>
              <p style={{
                fontSize: '12px',
                color: colors.textMuted,
                margin: 0,
              }}>
                {direction.categories.length} категорий
              </p>
            </div>

            {/* Счётчик товаров */}
            <div style={{
              padding: '6px 10px',
              background: `${direction.color}15`,
              border: `1px solid ${direction.color}25`,
              borderRadius: '8px',
            }}>
              <span style={{
                fontSize: '12px',
                fontWeight: 600,
                color: direction.color,
              }}>
                {direction.categories.reduce((sum, cat) => sum + cat.products.length, 0)}
              </span>
            </div>

            {/* Стрелка */}
            <ChevronRightIcon size={20} color={colors.textMuted} />
          </div>
        ))}
      </div>
    </div>
  )
}

// Экран 2: Категории
function CategoriesScreen({ 
  direction, 
  onBack, 
  onCategoryClick 
}: { 
  direction: Direction
  onBack: () => void
  onCategoryClick: (c: Category) => void
}) {
  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px 16px 100px',
    }}>
      {/* Header с кнопкой назад */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: colors.text,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            padding: '8px 0',
            marginBottom: '16px',
            cursor: 'pointer',
          }}
        >
          <BackIcon size={20} />
          Назад
        </button>

        <h1 style={{
          fontSize: '28px',
          fontWeight: 700,
          color: colors.text,
          margin: '0 0 8px 0',
        }}>
          {direction.name}
        </h1>
        <p style={{
          fontSize: '14px',
          color: colors.textMuted,
          margin: 0,
        }}>
          Выберите категорию
        </p>
      </div>

      {/* Список категорий */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}>
        {direction.categories.map(category => (
          <div
            key={category.id}
            onClick={() => onCategoryClick(category)}
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.cardBorder}`,
              borderRadius: '12px',
              padding: '18px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              transition: 'background 0.2s',
            }}
            onMouseDown={(e) => {
              const target = e.currentTarget as HTMLDivElement
              target.style.background = colors.nestedBg
            }}
            onMouseUp={(e) => {
              const target = e.currentTarget as HTMLDivElement
              target.style.background = colors.cardBg
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget as HTMLDivElement
              target.style.background = colors.cardBg
            }}
          >
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: 600,
                color: colors.text,
                margin: '0 0 4px 0',
              }}>
                {category.name}
              </h3>
              <p style={{
                fontSize: '12px',
                color: colors.textMuted,
                margin: '0 0 6px 0',
              }}>
                {category.description}
              </p>
              <span style={{
                fontSize: '11px',
                color: direction.color,
                fontWeight: 500,
              }}>
                {category.products.length} {category.products.length === 1 ? 'товар' : 'товара'}
              </span>
            </div>

            <ChevronRightIcon size={20} color={direction.color} />
          </div>
        ))}
      </div>
    </div>
  )
}

// Экран 3: Товары
function ProductsScreen({
  category,
  direction,
  onBack,
  onBuyProduct,
}: {
  category: Category
  direction: Direction
  onBack: () => void
  onBuyProduct: (p: Product) => void
}) {
  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px 16px 100px',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: colors.text,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            padding: '8px 0',
            marginBottom: '16px',
            cursor: 'pointer',
          }}
        >
          <BackIcon size={20} />
          Назад
        </button>

        <div style={{
          display: 'inline-block',
          padding: '6px 12px',
          background: `${direction.color}20`,
          border: `1px solid ${direction.color}40`,
          borderRadius: '8px',
          marginBottom: '12px',
        }}>
          <span style={{
            fontSize: '12px',
            color: direction.color,
            fontWeight: 500,
          }}>
            {direction.name}
          </span>
        </div>

        <h1 style={{
          fontSize: '24px',
          fontWeight: 700,
          color: colors.text,
          margin: '0 0 6px 0',
        }}>
          {category.name}
        </h1>
        <p style={{
          fontSize: '13px',
          color: colors.textMuted,
          margin: 0,
        }}>
          {category.description}
        </p>
      </div>

      {/* Товары */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}>
        {category.products.map(product => (
          <div
            key={product.id}
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.cardBorder}`,
              borderRadius: '12px',
              padding: '16px',
            }}
          >
            <h3 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: colors.text,
              margin: '0 0 6px 0',
            }}>
              {product.title}
            </h3>
            <p style={{
              fontSize: '13px',
              color: colors.textMuted,
              margin: '0 0 16px 0',
            }}>
              {product.description}
            </p>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <span style={{
                fontSize: '20px',
                fontWeight: 700,
                color: colors.text,
              }}>
                {product.price}
              </span>

              <button
                onClick={() => onBuyProduct(product)}
                style={{
                  padding: '10px 20px',
                  background: `${colors.accent}`,
                  border: 'none',
                  borderRadius: '10px',
                  color: colors.text,
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Купить
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
